import {
  Article,
  ArticleResolution,
} from '@wix/answers-api';
import classNames from 'classnames';
import React, { useState } from 'react';

import type { Step } from '../../pages/article/[articleTitle]';
import { ArticleABTestNotification } from '../ArticleABTestNotification';
import { ArticlePreviewNotification } from '../ArticlePreviewNotification';
import { BreadcrumbItem } from '../Breadcrumbs';
import { ArticleLeftPanel } from './ArticleLeftPanel';
import { ArticleMainContent } from './ArticleMainContent';
import { ArticleRightPanel } from './ArticleRightPanel';

import css from './index.module.scss';

export type ArticlePageLayoutProps = {
  article: Article;
  relatedArticles: Article[];
  steps: Step[];
  breadcrumbItems: BreadcrumbItem[];
  copilotArticleContentRef: React.RefObject<HTMLDivElement>;
  anchorsRef: React.RefObject<HTMLDivElement>;
  isLoading: boolean;
  selfArticleRedirect: (
    url: string,
    shallow?: boolean,
    targetLocale?: string
  ) => void;
  onClickStatus: (resolution: ArticleResolution) => Promise<void>;
};

export const ArticlePageLayout = ({
  article,
  relatedArticles,
  steps,
  breadcrumbItems,
  copilotArticleContentRef,
  anchorsRef,
  isLoading,
  selfArticleRedirect,
  onClickStatus,
}: ArticlePageLayoutProps) => {
  const [isLeftPanelCollapsed, setLeftPanelCollapsed] = useState(false);
  const [isRightPanelCollapsed, setRightPanelCollapsed] = useState(false);
  const hasSteps = steps.length > 0;

  return (
    <>
      <ArticlePreviewNotification />
      <ArticleABTestNotification article={article} />
      <div
        className={classNames(css.articlePageLayout, {
          [css.hasLeftPanel]: hasSteps,
          [css.hasRightPanel]: true,
        })}
      >
        {hasSteps && (
          <ArticleLeftPanel
            steps={steps}
            article={article}
            copilotArticleContentRef={copilotArticleContentRef}
            anchorsRef={anchorsRef}
            isCollapsed={isLeftPanelCollapsed || isLoading}
            onCollapse={() => setLeftPanelCollapsed(true)}
            onExpand={() => setLeftPanelCollapsed(false)}
          />
        )}
        <ArticleMainContent
          article={article}
          relatedArticles={relatedArticles}
          steps={steps}
          breadcrumbItems={breadcrumbItems}
          copilotArticleContentRef={copilotArticleContentRef}
          isLoading={isLoading}
          selfArticleRedirect={selfArticleRedirect}
          onClickStatus={onClickStatus}
        />
        <ArticleRightPanel
          selfArticleRedirect={selfArticleRedirect}
          isCollapsed={isRightPanelCollapsed}
          onCollapse={() => setRightPanelCollapsed(true)}
          onExpand={() => setRightPanelCollapsed(false)}
          suggestedQuestions={article.relatedQuestions as string[] | undefined}
        />
      </div>
    </>
  );
};
